/**
 * Payment Link Generator — three pages in one file (admin + customer + status).
 *
 *   /PG                — admin generator (passcode-gated, 4h session)
 *   /pl/:token         — customer-facing payment page
 *   /pl/:token/status  — outcome / receipt page (auto invoice on success)
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Lock, Loader2, Copy, Share2, MessageCircle, Mail, CheckCircle2, XCircle,
  AlertTriangle, ArrowRight, Plus, LogOut, ShieldCheck, Globe2, IndianRupee, Phone, MapPin,
  Trash2, Search, Clock, FileDown, Activity, X, ChevronDown, ChevronUp, Webhook,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const PG_TOKEN_KEY = "pg_admin_token";
const PG_EXP_KEY = "pg_admin_expires";

// ---------- Shared branding footer ----------
function PGFooter() {
  return (
    <div className="mt-10 pb-6 flex justify-center px-3" data-testid="pg-footer">
      <img src="/pg/footer.png" alt="Optivize · A Unit of SURAJIT MAJHI HUF · Payment Process by Razorpay & PayGlocal · Secure Payments" className="w-full max-w-4xl rounded-xl shadow-sm bg-white" />
    </div>
  );
}

function NoIndex() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex,nofollow";
    document.head.appendChild(m);
    return () => { try { document.head.removeChild(m); } catch {} };
  }, []);
  return null;
}

// ============================================================================
// Admin /PG page
// ============================================================================
export function PGAdmin() {
  const [token, setLocalToken] = useState(() => {
    const t = localStorage.getItem(PG_TOKEN_KEY);
    const exp = parseInt(localStorage.getItem(PG_EXP_KEY) || "0", 10);
    return t && exp > Date.now() ? t : null;
  });
  const [passcode, setPasscode] = useState("");
  const [authing, setAuthing] = useState(false);

  const login = async (e) => {
    e?.preventDefault?.();
    setAuthing(true);
    try {
      const { data } = await api.post("/pg/admin/auth", { passcode });
      const expMs = new Date(data.expires_at).getTime();
      localStorage.setItem(PG_TOKEN_KEY, data.token);
      localStorage.setItem(PG_EXP_KEY, String(expMs));
      setLocalToken(data.token);
      setPasscode("");
      toast.success("Welcome — session valid for 4 hours");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid passcode");
    } finally { setAuthing(false); }
  };

  const logout = () => {
    localStorage.removeItem(PG_TOKEN_KEY);
    localStorage.removeItem(PG_EXP_KEY);
    setLocalToken(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10 bg-gradient-to-br from-orange-50 via-white to-blue-50" data-testid="pg-passcode-gate">
        <NoIndex/>
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-xl ring-1 ring-orange-100 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-orange-500 via-orange-400 to-blue-600"/>
          <div className="p-7">
            <div className="flex flex-col items-center text-center mb-4">
              <img src="/optivize-logo-mark.png" alt="Optivize" className="h-10 w-auto mb-1.5"/>
              <div className="text-[10px] font-semibold tracking-wider text-slate-500 uppercase">A unit of <span className="text-blue-700">SURAJIT MAJHI HUF</span></div>
            </div>
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-md bg-orange-100 flex items-center justify-center"><Lock size={14} className="text-orange-600"/></div>
              <h1 className="text-base sm:text-lg font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Payment Link <span className="text-blue-600">Generator</span></h1>
            </div>
            <p className="text-sm text-slate-500 mb-5 text-center">Enter admin passcode to access. Sessions last 4 hours.</p>
            <form onSubmit={login} className="space-y-3">
              <input
                type="password" autoFocus inputMode="numeric" autoComplete="off"
                value={passcode}
                onChange={(e)=>setPasscode(e.target.value)}
                className="w-full px-3 py-3 text-xl tracking-[8px] font-mono text-center text-slate-900 placeholder:text-slate-400 bg-white border-2 border-slate-200 rounded-lg focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
                placeholder="• • • • • • • • • •"
                data-testid="pg-passcode-input"
              />
              <button type="submit" disabled={authing} className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold inline-flex items-center justify-center gap-2 shadow-sm transition-colors" data-testid="pg-passcode-submit">
                {authing ? <Loader2 size={16} className="animate-spin"/> : <ShieldCheck size={16}/>} Unlock
              </button>
            </form>
          </div>
        </div>
        <PGFooter/>
      </div>
    );
  }
  return <PGDashboard token={token} onLogout={logout}/>;
}

function PGDashboard({ token, onLogout }) {
  const [form, setForm] = useState({ gateway: "razorpay", amount: "", currency: "INR", product_name: "", notes: "" });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [links, setLinks] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterGateway, setFilterGateway] = useState("all");
  const [filterPayment, setFilterPayment] = useState("all");
  const [search, setSearch] = useState("");
  const [nowTick, setNowTick] = useState(Date.now());
  const [trackLink, setTrackLink] = useState(null);
  const authH = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterGateway !== "all") params.set("gateway", filterGateway);
      if (filterPayment !== "all") params.set("payment_status", filterPayment);
      if (search.trim()) params.set("q", search.trim());
      const { data } = await api.get(`/pg/admin/links?${params}`, { headers: authH });
      setLinks(data.rows || []);
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filterStatus, filterGateway, filterPayment, search]);
  useEffect(() => { load(); }, [load]);
  // Tick every second so the countdown updates live
  useEffect(() => { const t = setInterval(() => setNowTick(Date.now()), 1000); return () => clearInterval(t); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.product_name) { toast.error("Amount + product required"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/pg/admin/links", {
        gateway: form.gateway, amount: Number(form.amount),
        currency: form.currency, product_name: form.product_name, notes: form.notes,
      }, { headers: authH });
      setCreated(data);
      setForm({ ...form, amount: "", product_name: "", notes: "" });
      toast.success("Payment link created · valid for 24 hours");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create link");
    } finally { setCreating(false); }
  };

  const cancelLink = async (link) => {
    if (link.payment_status === "success") { toast.error("Paid link cannot be cancelled"); return; }
    if (!window.confirm(`Cancel link for "${link.product_name}"? This cannot be undone.`)) return;
    try {
      await api.post(`/pg/admin/links/${link.id}/cancel`, { reason: "Cancelled by admin" }, { headers: authH });
      toast.success("Link cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("Link copied"); };
  const shareWA = (url, name) => window.open(`https://wa.me/?text=${encodeURIComponent(`Hi! Your Optivize payment link for ${name}: ${url}`)}`, "_blank");
  const shareEmail = (url, name) => window.open(`mailto:?subject=${encodeURIComponent(`Optivize · Payment link for ${name}`)}&body=${encodeURIComponent(`Hi,\n\nPlease use the link below to complete your payment:\n${url}\n\nThanks,\nOptivize`)}`);
  const shareNative = async (url, name) => {
    if (navigator.share) {
      try { await navigator.share({ title: "Optivize · Payment", text: `Payment link for ${name}`, url }); }
      catch {}
    } else { copy(url); }
  };

  const isInr = false; // Razorpay & PayGlocal both support multi-currency
  const CURRENCIES_ALL = ["INR","USD","GBP","EUR","CAD","AUD","SGD"];
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50/40 via-white to-blue-50/40 py-6 px-3" data-testid="pg-dashboard">
      <NoIndex/>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/optivize-logo-mark.png" alt="Optivize" className="h-9 sm:h-10 w-auto shrink-0"/>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-bold text-slate-900 truncate" style={{fontFamily:"Outfit"}}>Payment Link <span className="text-orange-500">Generator</span></h1>
              <p className="text-[10px] sm:text-[11px] font-semibold tracking-wider text-slate-500 uppercase truncate">A unit of <span className="text-blue-700">SURAJIT MAJHI HUF</span></p>
            </div>
          </div>
          <button onClick={onLogout} className="text-xs text-slate-500 hover:text-orange-600 inline-flex items-center gap-1 transition-colors shrink-0" data-testid="pg-logout"><LogOut size={12}/> Sign out</button>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm ring-1 ring-orange-100 border-t-2 border-orange-500 p-5 sm:p-6 mb-6 space-y-4" data-testid="pg-create-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">Payment Gateway</label>
              <div className="grid grid-cols-2 gap-2">
                {["razorpay","payglocal"].map(g => (
                  <button type="button" key={g}
                    onClick={()=>setForm({...form, gateway: g})}
                    className={`py-2.5 rounded-lg text-sm font-semibold capitalize transition-all ${form.gateway === g ? "bg-blue-600 text-white shadow-sm" : "bg-slate-100 text-slate-700 hover:bg-blue-50 hover:text-blue-700"}`}
                    data-testid={`pg-gw-${g}`}>{g === "razorpay" ? "Razorpay" : "PayGlocal"}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Currency</label>
                <span className="text-[10px] text-slate-400">Multi-currency · both gateways</span>
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="pg-currency">
                {CURRENCIES_ALL.map(c => {
                  const disabled = isInr && c !== "INR";
                  const selected = form.currency === c;
                  return (
                    <button type="button" key={c}
                      disabled={disabled}
                      onClick={()=>setForm({...form, currency: c})}
                      data-testid={`pg-currency-${c}`}
                      className={`px-2.5 py-1.5 rounded-md text-xs font-semibold transition-all ${
                        selected ? "bg-orange-500 text-white shadow-sm" :
                        disabled ? "bg-slate-50 text-slate-300 cursor-not-allowed" :
                        "bg-white border border-slate-200 text-slate-700 hover:border-orange-400 hover:text-orange-600"
                      }`}>{c}</button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">Amount</label>
              <input type="number" min="1" step="0.01" value={form.amount} onChange={(e)=>setForm({...form, amount: e.target.value})} placeholder="1000.00" className="w-full px-3 py-2.5 bg-white text-slate-900 placeholder:text-slate-400 border border-slate-300 rounded-lg focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none" data-testid="pg-amount"/>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">Product / Service</label>
              <input value={form.product_name} onChange={(e)=>setForm({...form, product_name: e.target.value})} placeholder="Website Design — Premium" className="w-full px-3 py-2.5 bg-white text-slate-900 placeholder:text-slate-400 border border-slate-300 rounded-lg focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none" data-testid="pg-product"/>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">Notes (optional, internal)</label>
              <input value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="w-full px-3 py-2.5 bg-white text-slate-900 placeholder:text-slate-400 border border-slate-300 rounded-lg focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none" data-testid="pg-notes"/>
            </div>
          </div>
          <button type="submit" disabled={creating} className="w-full sm:w-auto px-6 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg font-semibold inline-flex items-center justify-center gap-2 shadow-sm transition-colors" data-testid="pg-generate">
            {creating ? <Loader2 size={16} className="animate-spin"/> : <Plus size={16}/>} Generate Link
          </button>
        </form>

        {created && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-6" data-testid="pg-created-banner">
            <div className="text-blue-700 font-semibold inline-flex items-center gap-2 mb-2"><CheckCircle2 size={16} className="text-orange-500"/> Link ready · {created.invoice_number || created.id?.slice(0,8).toUpperCase()}</div>
            <div className="flex gap-2 items-center">
              <input readOnly value={created.share_url} className="flex-1 px-3 py-2 border border-blue-200 rounded-lg text-xs font-mono bg-white text-slate-900" data-testid="pg-created-url"/>
              <button onClick={()=>copy(created.share_url)} className="px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs inline-flex items-center gap-1" data-testid="pg-copy"><Copy size={12}/> Copy</button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button onClick={()=>shareWA(created.share_url, created.product_name)} className="px-3 py-1.5 bg-[#25D366] text-white rounded text-xs inline-flex items-center gap-1" data-testid="pg-share-wa"><MessageCircle size={12}/> WhatsApp</button>
              <button onClick={()=>shareEmail(created.share_url, created.product_name)} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-800 text-white rounded text-xs inline-flex items-center gap-1" data-testid="pg-share-email"><Mail size={12}/> Email</button>
              <button onClick={()=>shareNative(created.share_url, created.product_name)} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs inline-flex items-center gap-1" data-testid="pg-share-native"><Share2 size={12}/> Share</button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-blue-100 border-t-2 border-blue-500 p-5 mb-6" data-testid="pg-history">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-sm font-bold text-slate-800">Generated links ({links.length})</h2>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search product, name, email, phone…"
                className="pl-7 pr-3 py-1.5 bg-white text-slate-900 placeholder:text-slate-400 border border-slate-300 rounded-md text-xs w-64" data-testid="pg-search"/>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-3 text-xs">
            <span className="text-slate-500 self-center mr-1">Status:</span>
            {["all","active","paid","cancelled","expired"].map(s=>(
              <button key={s} onClick={()=>setFilterStatus(s)} data-testid={`pg-filter-status-${s}`}
                className={`px-2.5 py-1 rounded-full transition-colors ${filterStatus===s ? "bg-orange-500 text-white shadow-sm" : "bg-slate-100 text-slate-700 hover:bg-orange-50 hover:text-orange-600"} capitalize`}>{s}</button>
            ))}
            <span className="text-slate-500 self-center ml-3 mr-1">Gateway:</span>
            {["all","razorpay","payglocal"].map(g=>(
              <button key={g} onClick={()=>setFilterGateway(g)} data-testid={`pg-filter-gateway-${g}`}
                className={`px-2.5 py-1 rounded-full transition-colors ${filterGateway===g ? "bg-blue-600 text-white shadow-sm" : "bg-slate-100 text-slate-700 hover:bg-blue-50 hover:text-blue-700"} capitalize`}>{g}</button>
            ))}
            <span className="text-slate-500 self-center ml-3 mr-1">Payment:</span>
            {["all","pending","success","failed","cancelled","declined"].map(p=>(
              <button key={p} onClick={()=>setFilterPayment(p)} data-testid={`pg-filter-payment-${p}`}
                className={`px-2.5 py-1 rounded-full transition-colors ${filterPayment===p ? "bg-orange-500 text-white shadow-sm" : "bg-slate-100 text-slate-700 hover:bg-orange-50 hover:text-orange-600"} capitalize`}>{p}</button>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-slate-700">
              <thead className="text-slate-500 text-left border-b border-slate-200">
                <tr><th className="py-2 pr-2">Created</th><th>Product</th><th>Amount</th><th>Gateway</th><th>Status</th><th>Payment</th><th>Validity</th><th>Customer</th><th className="text-right">Actions</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {links.map(l => {
                  const statusCls = {
                    active: "bg-blue-100 text-blue-700",
                    paid: "bg-emerald-100 text-emerald-700",
                    cancelled: "bg-slate-200 text-slate-600",
                    expired: "bg-rose-100 text-rose-700",
                  }[l.status] || "bg-slate-100 text-slate-600";
                  return (
                  <tr key={l.token} data-testid={`pg-row-${l.token.slice(0,8)}`}>
                    <td className="py-2 pr-2 text-slate-500 whitespace-nowrap">{(l.created_at||"").slice(0,16).replace("T"," ")}</td>
                    <td className="text-slate-800 font-medium">{l.product_name}</td>
                    <td className="whitespace-nowrap">{l.currency} {l.amount}</td>
                    <td className="capitalize">{l.gateway}</td>
                    <td><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${statusCls}`} data-testid={`pg-status-${l.token.slice(0,8)}`}>{l.status}</span></td>
                    <td>
                      <span className={
                        l.payment_status === "success" ? "text-emerald-600" :
                        l.payment_status === "failed" || l.payment_status === "declined" ? "text-rose-600" :
                        "text-amber-600"
                      }>{l.payment_status}</span>
                    </td>
                    <td className="text-slate-600 whitespace-nowrap" data-testid={`pg-validity-${l.token.slice(0,8)}`}>
                      <ValidityCountdown link={l} now={nowTick}/>
                    </td>
                    <td className="text-slate-600 align-top">
                      {l.customer?.email ? <div className="text-slate-700">{l.customer.email}</div> : "—"}
                      {l.client_meta && (
                        <div className="text-[10px] text-slate-400 mt-0.5 leading-tight" data-testid={`pg-geo-${l.token.slice(0,8)}`}>
                          {l.client_meta.geo?.city ? (
                            <>
                              <span className="font-semibold text-slate-500">📍 {[l.client_meta.geo.city, l.client_meta.geo.region, l.client_meta.geo.country_code].filter(Boolean).join(", ")}</span>
                              <br/><span title={l.client_meta.user_agent}>{l.client_meta.ip}</span>
                            </>
                          ) : (
                            <>opened from {l.client_meta.ip || "—"}</>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <button onClick={()=>copy(`${window.location.origin}/pl/${l.token}`)} className="text-blue-600 hover:underline mr-2" data-testid={`pg-copy-${l.token.slice(0,8)}`}>copy</button>
                      <button onClick={()=>setTrackLink(l)} className="text-indigo-600 hover:underline mr-2 inline-flex items-center gap-0.5" data-testid={`pg-track-${l.token.slice(0,8)}`}><Activity size={11}/> track</button>
                      {l.payment_status === "success" && l.invoice_id && (
                        <a href={`/api/pg/links/${l.token}/invoice.pdf`} download className="text-emerald-600 hover:underline mr-2 inline-flex items-center gap-0.5" data-testid={`pg-invoice-${l.token.slice(0,8)}`}><FileDown size={11}/> invoice</a>
                      )}
                      {l.status === "active" && l.payment_status !== "success" && (
                        <button onClick={()=>cancelLink(l)} className="text-rose-600 hover:underline inline-flex items-center gap-0.5" data-testid={`pg-cancel-${l.token.slice(0,8)}`}><Trash2 size={11}/> cancel</button>
                      )}
                    </td>
                  </tr>
                )})}
                {links.length === 0 && (<tr><td colSpan={9} className="py-6 text-center text-slate-400 italic">No links match the current filters.</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <PGFooter/>
      </div>
      {trackLink && <TrackModal link={trackLink} authH={authH} onClose={()=>setTrackLink(null)}/>}
    </div>
  );
}


// ============================================================================
// Payment tracking modal — timeline + gateway/webhook payloads
// ============================================================================
const EVENT_META = {
  "link.created":              { label: "Link created",                color: "bg-blue-500" },
  "page.viewed":               { label: "Payment page opened",         color: "bg-slate-400" },
  "checkout.initiated":        { label: "Checkout initiated",          color: "bg-amber-500" },
  "gateway.order_created":     { label: "Gateway order created",       color: "bg-blue-500" },
  "gateway.session_created":   { label: "Gateway session created",     color: "bg-blue-500" },
  "gateway.callback_received": { label: "Gateway callback received",   color: "bg-indigo-500" },
  "gateway.webhook_received":  { label: "Gateway webhook received",    color: "bg-indigo-600" },
  "gateway.error":             { label: "Gateway error",               color: "bg-rose-500" },
  "payment.success":           { label: "Payment successful",          color: "bg-emerald-500" },
  "payment.failed":            { label: "Payment failed",              color: "bg-rose-500" },
  "payment.declined":          { label: "Payment declined",            color: "bg-rose-500" },
  "payment.cancelled":         { label: "Payment cancelled",           color: "bg-slate-500" },
  "invoice.emailed":           { label: "Invoice emailed to customer", color: "bg-emerald-400" },
  "partner_webhook.delivered": { label: "Partner webhook delivered",   color: "bg-cyan-500" },
};

function EventRow({ ev }) {
  const [open, setOpen] = useState(false);
  const meta = EVENT_META[ev.event] || { label: ev.event, color: "bg-slate-400" };
  const hasDetail = ev.detail && Object.keys(ev.detail).length > 0;
  const summaryBits = [];
  const d = ev.detail || {};
  if (d.gateway) summaryBits.push(d.gateway);
  if (d.webhook_event) summaryBits.push(d.webhook_event);
  if (d.pg_status) summaryBits.push(d.pg_status);
  if (d.razorpay_order_id) summaryBits.push(d.razorpay_order_id);
  if (d.razorpay_payment_id) summaryBits.push(d.razorpay_payment_id);
  if (d.transaction_id) summaryBits.push(`txn ${d.transaction_id}`);
  if (d.invoice_number) summaryBits.push(d.invoice_number);
  if (d.customer_email) summaryBits.push(d.customer_email);
  if (d.status_code) summaryBits.push(`HTTP ${d.status_code}`);
  if (d.signature_valid === false) summaryBits.push("⚠ INVALID SIGNATURE");
  if (d.error) summaryBits.push(d.error);
  return (
    <div className="relative pl-6 pb-4 last:pb-0">
      <span className={`absolute left-0 top-1 w-3 h-3 rounded-full ring-4 ring-white ${meta.color}`}/>
      <span className="absolute left-[5px] top-4 bottom-0 w-px bg-slate-200"/>
      <div className="flex flex-wrap items-center gap-x-2">
        <span className="text-xs font-bold text-slate-800">{meta.label}</span>
        {ev.source && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{ev.source}</span>}
        <span className="text-[10px] text-slate-400 font-mono">{(ev.at||"").slice(0,19).replace("T"," ")} UTC</span>
        {hasDetail && (
          <button onClick={()=>setOpen(o=>!o)} className="text-[10px] text-blue-600 inline-flex items-center gap-0.5" data-testid={`pg-ev-toggle-${ev.id?.slice(0,6)}`}>
            {open ? <ChevronUp size={10}/> : <ChevronDown size={10}/>} raw
          </button>
        )}
      </div>
      {summaryBits.length > 0 && <div className="text-[11px] text-slate-500 mt-0.5 break-all">{summaryBits.join(" · ")}</div>}
      {open && hasDetail && (
        <pre className="mt-1.5 bg-slate-900 text-emerald-300 text-[10px] rounded-lg p-2.5 overflow-x-auto max-h-52">{JSON.stringify(ev.detail, null, 2)}</pre>
      )}
    </div>
  );
}

function TrackModal({ link, authH, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/pg/admin/links/${link.id || link.token}/events`, { headers: authH })
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Failed to load tracking"));
  }, [link, authH]); // eslint-disable-line react-hooks/exhaustive-deps
  const l = data?.link || link;
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl p-6 my-6" onClick={e=>e.stopPropagation()} data-testid="pg-track-modal">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 inline-flex items-center gap-1"><Activity size={11}/> Payment tracking</div>
            <h3 className="font-bold text-slate-900">{l.product_name}</h3>
            <div className="text-xs text-slate-500 mt-0.5 font-mono">{l.token?.slice(0,18)}… · {l.currency} {l.amount} · {l.gateway}
              {l.source === "api" || l.api_key_id ? " · via Developer API" : ""}</div>
          </div>
          <button onClick={onClose} data-testid="pg-track-close"><X size={18} className="text-slate-400"/></button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${l.payment_status==="success"?"bg-emerald-100 text-emerald-700":["failed","declined"].includes(l.payment_status)?"bg-rose-100 text-rose-700":"bg-amber-100 text-amber-700"}`} data-testid="pg-track-status">{l.payment_status}</span>
          {l.transaction_id && <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">txn {l.transaction_id}</span>}
          {l.invoice_number && <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{l.invoice_number}</span>}
          {l.payment_status === "success" && l.invoice_id && (
            <a href={`/api/pg/links/${l.token}/invoice.pdf`} download className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white inline-flex items-center gap-1" data-testid="pg-track-invoice"><FileDown size={10}/> Invoice PDF</a>
          )}
        </div>
        <div className="mt-5">
          {err ? <div className="text-sm text-rose-600">{err}</div> :
           !data ? <Loader2 className="animate-spin mx-auto my-8 text-indigo-500"/> :
           data.events.length === 0 ? (
            <div className="text-sm text-slate-400 italic bg-slate-50 rounded-lg p-4">
              No detailed events recorded — this link was created before live tracking was enabled.
              Current state: {l.status} / {l.payment_status}{l.paid_at ? ` · paid at ${l.paid_at.slice(0,19).replace("T"," ")}` : ""}.
            </div>
           ) : (
            <div data-testid="pg-track-timeline">
              {data.events.map(ev => <EventRow key={ev.id} ev={ev}/>)}
            </div>
           )}
          {data?.partner_webhooks?.length > 0 && (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <div className="text-xs font-bold text-slate-700 inline-flex items-center gap-1.5 mb-2"><Webhook size={12} className="text-cyan-600"/> Partner webhook deliveries (Developer API)</div>
              {data.partner_webhooks.map(w => (
                <div key={w.id} className="text-[11px] text-slate-600 bg-cyan-50/60 border border-cyan-100 rounded-lg px-3 py-1.5 mb-1 break-all">
                  <b>{w.event}</b> → {w.url} · HTTP {w.status_code ?? "—"} · {w.attempts} attempt{w.attempts>1?"s":""} · {(w.at||"").slice(0,19).replace("T"," ")}{w.error ? ` · ${w.error}` : ""}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// Customer /pay/:token
// ============================================================================
const COUNTRIES = [["IN","India"],["US","United States"],["GB","United Kingdom"],["CA","Canada"],["AU","Australia"],["AE","UAE"],["SG","Singapore"]];

function ValidityCountdown({ link, now }) {
  if (link.status === "paid") return <span className="text-emerald-600 text-[11px]">—</span>;
  if (link.status === "cancelled") return <span className="text-slate-400 text-[11px]">cancelled</span>;
  if (link.status === "expired") return <span className="text-rose-500 text-[11px]">expired</span>;
  if (!link.expires_at) return <span className="text-slate-400 text-[11px]">—</span>;
  const diffMs = new Date(link.expires_at).getTime() - now;
  if (diffMs <= 0) return <span className="text-rose-500 text-[11px]">expired</span>;
  const h = Math.floor(diffMs / 3600000);
  const m = Math.floor((diffMs % 3600000) / 60000);
  const s = Math.floor((diffMs % 60000) / 1000);
  const danger = diffMs < 3600000; // < 1h
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-mono ${danger ? "text-rose-600" : "text-slate-600"}`}>
      <Clock size={10}/> {h.toString().padStart(2,"0")}:{m.toString().padStart(2,"0")}:{s.toString().padStart(2,"0")}
    </span>
  );
}

export function PGCustomerPay() {
  const { token } = useParams();
  const nav = useNavigate();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    first_name: "", last_name: "",
    phone: "", email: "",
    address_street: "", address_city: "", address_state: "", address_country: "IN",
    postal_code: "",
    amount: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [autoMode, setAutoMode] = useState(false);

  const startPayment = useCallback(async (payForm, linkData) => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/pg/links/${token}/checkout`, {
        ...payForm, amount: Number(payForm.amount),
      });
      if (data.gateway === "razorpay") {
        // Load Razorpay JS
        await new Promise((resolve) => {
          if (window.Razorpay) return resolve();
          const s = document.createElement("script"); s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = resolve; document.body.appendChild(s);
        });
        const rzp = new window.Razorpay({
          key: data.razorpay_key_id, order_id: data.razorpay_order_id,
          amount: data.amount, currency: data.currency,
          name: "Optivize", description: linkData.product_name,
          prefill: { name: `${payForm.first_name} ${payForm.last_name}`.trim(), email: payForm.email, contact: payForm.phone },
          handler: async (resp) => {
            try {
              await api.post(`/pg/links/${token}/razorpay/confirm`, {
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_signature: resp.razorpay_signature,
              });
            } catch {}
            nav(`/pl/${token}/status`);
          },
          modal: { ondismiss: () => nav(`/pl/${token}/status?cancelled=1`) },
        });
        rzp.open();
      } else if (data.redirect_url) {
        window.location.href = data.redirect_url;
        return; // keep spinner during redirect
      } else {
        toast.error("Gateway did not return a redirect URL");
        setAutoMode(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment initiation failed");
      setAutoMode(false);
    } finally { setSubmitting(false); }
  }, [token, nav]);

  useEffect(() => {
    let alive = true;
    api.get(`/pg/links/${token}`)
      .then(r => { if (alive) {
        setLink(r.data);
        const pf = r.data.prefill || {};
        let first = pf.first_name || "", last = pf.last_name || "";
        if (!first && pf.name) { const parts = pf.name.trim().split(/\s+/); first = parts[0]; last = parts.slice(1).join(" "); }
        const nf = {
          amount: String(r.data.amount),
          first_name: first, last_name: last || first,
          email: pf.email || "", phone: pf.phone || "",
          address_street: pf.address || "",
          address_city: pf.city || "",
          address_state: pf.state || "",
          postal_code: pf.pin_code || "",
          address_country: pf.country || "IN",
        };
        setForm(f => ({ ...f, ...nf, last_name: last || (first ? first : f.last_name) }));
        const required = ["first_name", "last_name", "phone", "email", "address_street", "address_city", "address_state"];
        if (r.data.auto_checkout && r.data.payment_status === "pending" && r.data.status === "active"
            && required.every(k => (nf[k] || "").trim())) {
          setAutoMode(true);
          setTimeout(() => { if (alive) startPayment(nf, r.data); }, 800);
        }
      }})
      .catch(e => toast.error(e?.response?.data?.detail || "Link not found"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, startPayment]);

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const required = ["first_name","last_name","phone","email","address_street","address_city","address_state"];
    if (required.some(k => !form[k]?.trim())) { toast.error("Please fill all required fields"); return; }
    await startPayment(form, link);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-blue-50"><Loader2 className="animate-spin text-orange-500" size={26}/></div>;
  if (!link) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10 bg-gradient-to-br from-orange-50 via-white to-blue-50">
      <NoIndex/>
      <AlertTriangle size={36} className="text-rose-500 mb-3"/>
      <h1 className="text-xl font-bold text-slate-900">Link not available</h1>
      <p className="text-slate-500 text-sm mt-1">This payment link is invalid, expired or already paid.</p>
      <PGFooter/>
    </div>
  );

  if (autoMode) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10 bg-gradient-to-br from-orange-50 via-white to-blue-50" data-testid="pg-auto-checkout">
      <NoIndex/>
      <div className="bg-white rounded-2xl shadow-md ring-1 ring-orange-100 p-8 max-w-md w-full text-center">
        <img src="/optivize-logo-mark.png" alt="Optivize" className="h-10 w-auto mx-auto mb-4"/>
        <Loader2 className="animate-spin text-orange-500 mx-auto" size={30}/>
        <h1 className="text-lg font-bold text-slate-900 mt-4" style={{fontFamily:"Outfit"}}>Taking you to secure checkout…</h1>
        <p className="text-sm text-slate-500 mt-1">{link.product_name} · {link.currency} {link.amount}</p>
        <p className="text-[11px] text-slate-400 mt-3">Your details were provided by the merchant. You&rsquo;ll complete payment on the {link.gateway} secure page.</p>
      </div>
      <PGFooter/>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50/60 via-white to-blue-50/60 py-6 px-3" data-testid="pg-customer-pay">
      <NoIndex/>
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-md ring-1 ring-orange-100 overflow-hidden mb-4">
          <div className="h-1.5 bg-gradient-to-r from-orange-500 via-orange-400 to-blue-600"/>
          <div className="p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3 pb-3 border-b border-slate-100">
              <img src="/optivize-logo-mark.png" alt="Optivize" className="h-9 sm:h-10 w-auto"/>
              <div className="min-w-0">
                <div className="text-sm font-bold text-slate-800 truncate" style={{fontFamily:"Outfit"}}>Optivize</div>
                <div className="text-[10px] font-semibold tracking-wider text-slate-500 uppercase truncate">A unit of <span className="text-blue-700">SURAJIT MAJHI HUF</span></div>
              </div>
            </div>
            <div className="flex items-center justify-between mb-1">
              <h1 className="text-lg sm:text-xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Complete your <span className="text-orange-500">payment</span></h1>
              <span className={`text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2.5 py-1 rounded-full ${link.gateway === "razorpay" ? "bg-blue-50 text-blue-700" : "bg-orange-50 text-orange-700"}`}>
                <Globe2 size={11}/>
                {link.gateway}
              </span>
            </div>
            <p className="text-sm text-slate-500">{link.product_name}</p>
            {link.expires_at && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-full text-[11px] font-semibold text-orange-800" data-testid="pg-cust-validity">
                <Clock size={11}/> Valid for 24h · expires {new Date(link.expires_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl shadow-md ring-1 ring-blue-100 p-5 sm:p-6 space-y-4" data-testid="pg-customer-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Row label="First name (as on card) *"><input value={form.first_name} onChange={setF("first_name")} required autoComplete="given-name" className="w-full input-cust" data-testid="pg-cust-first-name"/></Row>
            <Row label="Last name (as on card) *"><input value={form.last_name} onChange={setF("last_name")} required autoComplete="family-name" className="w-full input-cust" data-testid="pg-cust-last-name"/></Row>
          </div>
          <p className="text-[11px] text-slate-500 -mt-2 leading-snug" data-testid="pg-cust-name-hint">
            <span className="font-semibold text-orange-600">Tip:</span> Please type your name <em>exactly</em> as printed on your card — same spelling, no initials. Mismatches are the #1 reason card payments fail.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Row label="Phone *"><input value={form.phone} onChange={setF("phone")} required inputMode="tel" className="w-full input-cust" data-testid="pg-cust-phone"/></Row>
            <Row label="Email *"><input type="email" value={form.email} onChange={setF("email")} required className="w-full input-cust" data-testid="pg-cust-email"/></Row>
          </div>
          <Row label="Street address *"><input value={form.address_street} onChange={setF("address_street")} required className="w-full input-cust" data-testid="pg-cust-street"/></Row>
          <div className="grid sm:grid-cols-4 gap-3">
            <Row label="City *"><input value={form.address_city} onChange={setF("address_city")} required className="w-full input-cust" data-testid="pg-cust-city"/></Row>
            <Row label="State *"><input value={form.address_state} onChange={setF("address_state")} required className="w-full input-cust" data-testid="pg-cust-state"/></Row>
            <Row label="Postal / Zip"><input value={form.postal_code} onChange={setF("postal_code")} placeholder={form.address_country === "IN" ? "560094" : "ZIP"} className="w-full input-cust" data-testid="pg-cust-postal"/></Row>
            <Row label="Country *">
              <select value={form.address_country} onChange={setF("address_country")} className="w-full input-cust" data-testid="pg-cust-country">
                {COUNTRIES.map(([c,n])=> <option key={c} value={c}>{n}</option>)}
              </select>
            </Row>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Row label={`Amount (${link.currency}) — editable`}><input type="number" min="1" step="0.01" value={form.amount} onChange={setF("amount")} className="w-full input-cust font-bold text-lg text-orange-600" data-testid="pg-cust-amount"/></Row>
            <Row label="Currency (fixed)"><input readOnly value={link.currency} className="w-full input-cust bg-blue-50 text-blue-700 font-bold text-lg cursor-not-allowed"/></Row>
          </div>
          <button type="submit" disabled={submitting} className="w-full py-3.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg font-bold inline-flex items-center justify-center gap-2 text-base shadow-sm transition-colors" data-testid="pg-pay-btn">
            {submitting ? <Loader2 size={18} className="animate-spin"/> : <ShieldCheck size={18}/>} Pay {link.currency} {form.amount}
          </button>
          <p className="text-[11px] text-slate-400 text-center">You&rsquo;ll be redirected to {link.gateway} hosted checkout. Your card data never touches our servers.</p>
        </form>

        <PGFooter/>
      </div>
      <style>{`.input-cust{padding:0.6rem 0.8rem;border:1px solid #e2e8f0;border-radius:0.5rem;font-size:14px;transition:border-color .15s ease,box-shadow .15s ease;background:#fff;color:#0f172a}.input-cust::placeholder{color:#94a3b8}.input-cust:focus{outline:none;border-color:#fb923c;box-shadow:0 0 0 3px rgba(251,146,60,.15)}`}</style>
    </div>
  );
}

function Row({ label, children }) {
  return (<div><label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">{label}</label>{children}</div>);
}


// ============================================================================
// Status /pay/:token/status
// ============================================================================
export function PGStatus() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [link, setLink] = useState(null);
  const [polling, setPolling] = useState(true);
  const cancelled = params.get("cancelled") === "1";
  const failed = params.get("failed") === "1";

  useEffect(() => {
    let attempts = 0;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/pg/links/${token}/status`);
        if (!alive) return;
        setLink(data);
        if (data.payment_status === "success" || attempts >= 14) { setPolling(false); return; }
      } catch {}
      attempts++;
      if (alive && attempts < 15) setTimeout(tick, 2500);
      else setPolling(false);
    };
    const plinkId = params.get("razorpay_payment_link_id");
    if (plinkId && params.get("razorpay_signature")) {
      // Returned from a Razorpay-hosted payment link — verify + finalize first
      api.post(`/pg/links/${token}/plink/confirm`, {
        razorpay_payment_id: params.get("razorpay_payment_id"),
        razorpay_payment_link_id: plinkId,
        razorpay_payment_link_reference_id: params.get("razorpay_payment_link_reference_id"),
        razorpay_payment_link_status: params.get("razorpay_payment_link_status"),
        razorpay_signature: params.get("razorpay_signature"),
      }).catch(() => {}).finally(tick);
    } else {
      tick();
    }
    return () => { alive = false; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const status = cancelled ? "cancelled" : (link?.payment_status || (failed ? "failed" : "pending"));
  const icon = status === "success" ? <CheckCircle2 size={52} className="text-emerald-500 mx-auto"/> : (["failed","declined","cancelled"].includes(status) ? <XCircle size={52} className="text-rose-500 mx-auto"/> : <Loader2 size={52} className="text-amber-500 mx-auto animate-spin"/>);
  const title = { success: "Payment successful", failed: "Payment failed", declined: "Payment declined", cancelled: "Payment cancelled", pending: "Payment pending" }[status] || "Status";

  // Merchant return redirect (Developer API redirect_url) — countdown then go
  const [returnSecs, setReturnSecs] = useState(null);
  useEffect(() => {
    if (!link?.merchant_redirect_url || !["success","failed","declined","cancelled"].includes(status)) return;
    setReturnSecs(6);
    const iv = setInterval(() => setReturnSecs(s => {
      if (s <= 1) { clearInterval(iv); window.location.href = link.merchant_redirect_url; return 0; }
      return s - 1;
    }), 1000);
    return () => clearInterval(iv);
  }, [link?.merchant_redirect_url, status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-download invoice on success
  useEffect(() => {
    if (status === "success" && link?.invoice_id) {
      const url = `/api/pg/links/${token}/invoice.pdf`;
      const a = document.createElement("a");
      a.href = url; a.download = `${link.invoice_number || "invoice"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    }
  }, [status, link, token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50/60 via-white to-blue-50/60 py-6 px-3" data-testid="pg-status-page">
      <NoIndex/>
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-2xl shadow-md ring-1 ring-orange-100 overflow-hidden text-center" data-testid={`pg-status-${status}`}>
          <div className={`h-1.5 ${status === "success" ? "bg-gradient-to-r from-orange-500 via-emerald-400 to-blue-600" : ["failed","declined","cancelled"].includes(status) ? "bg-gradient-to-r from-rose-500 to-orange-400" : "bg-gradient-to-r from-orange-500 via-orange-400 to-blue-600"}`}/>
          <div className="p-6 sm:p-8">
            <div className="flex items-center justify-center gap-3 mb-4 pb-4 border-b border-slate-100">
              <img src="/optivize-logo-mark.png" alt="Optivize" className="h-9 w-auto"/>
              <div className="text-left">
                <div className="text-sm font-bold text-slate-800 leading-none" style={{fontFamily:"Outfit"}}>Optivize</div>
                <div className="text-[10px] font-semibold tracking-wider text-slate-500 uppercase">A unit of <span className="text-blue-700">SURAJIT MAJHI HUF</span></div>
              </div>
            </div>
            {polling && status === "pending" ? <Loader2 size={52} className="text-orange-500 mx-auto animate-spin"/> : icon}
            <h1 className="text-2xl font-bold text-slate-900 mt-3" style={{fontFamily:"Outfit"}}>{title}</h1>
            {link && (
              <div className="text-left mt-5 space-y-1.5 text-sm" data-testid="pg-status-details">
                <DetailRow k="Customer" v={link.customer?.name}/>
                <DetailRow k="Email" v={link.customer?.email}/>
                <DetailRow k="Phone" v={link.customer?.phone}/>
                <DetailRow k="Product" v={link.product_name}/>
                <DetailRow k="Amount" v={`${link.currency} ${link.amount}`}/>
                <DetailRow k="Gateway" v={link.gateway === "payglocal" ? "PayGlocal" : link.gateway === "razorpay" ? "Razorpay" : link.gateway}/>
                {link.transaction_id && <DetailRow k="Transaction ID" v={link.transaction_id}/>}
                {link.invoice_number && <DetailRow k="Invoice #" v={link.invoice_number}/>}
                <DetailRow k="Date" v={(link.paid_at || link.created_at || "").slice(0,19).replace("T"," ")}/>
              </div>
            )}
            {status === "success" && link?.invoice_id && (
              <a href={`/api/pg/links/${token}/invoice.pdf`} download className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold text-sm shadow-sm transition-colors" data-testid="pg-download-invoice">
                Download Invoice PDF <ArrowRight size={14}/>
              </a>
            )}
            {["failed","declined","cancelled"].includes(status) && (
              <Link to={`/pl/${token}`} className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm shadow-sm transition-colors" data-testid="pg-retry-btn">Try again <ArrowRight size={14}/></Link>
            )}
            {link?.merchant_redirect_url && returnSecs !== null && (
              <div className="mt-5 rounded-xl bg-blue-50 border border-blue-100 p-3" data-testid="pg-merchant-redirect">
                <div className="text-xs text-slate-600">Returning you to the merchant&rsquo;s website{returnSecs > 0 ? ` in ${returnSecs}s…` : "…"}</div>
                <a href={link.merchant_redirect_url} className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-xs transition-colors" data-testid="pg-merchant-redirect-btn">
                  Return to merchant now <ArrowRight size={13}/>
                </a>
              </div>
            )}
          </div>
        </div>
        <PGFooter/>
      </div>
    </div>
  );
}

function DetailRow({ k, v }) {
  if (!v) return null;
  return (<div className="flex justify-between border-b border-slate-100 py-1.5"><span className="text-slate-500">{k}</span><span className="font-semibold text-slate-800 break-all text-right">{v}</span></div>);
}

export default PGAdmin;
